import type { ReactNode } from 'react';
import { ACCENTS, type Accent } from './accent';

/**
 * Card with a heading. `accent` colours the bar beside the title (module
 * colours: plan teal, recipes orange, pantry green, groceries teal→green);
 * `icon` puts a tinted tile before it; `action` sits at the right of the
 * header (usually a "View all" link).
 */
export function Section({
  title,
  description,
  children,
  id,
  accent = 'teal',
  icon,
  action,
  className = '',
}: {
  title: string;
  description?: string | undefined;
  children: ReactNode;
  id?: string | undefined;
  accent?: Accent;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const headingId = id ?? title.toLowerCase().replace(/\s+/g, '-');
  const a = ACCENTS[accent];
  return (
    <section aria-labelledby={headingId} className={`card p-4 sm:p-5 ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          {icon ? (
            <span
              aria-hidden
              className={`flex size-9 shrink-0 items-center justify-center rounded-xl ${a.soft} ${a.text}`}
            >
              {icon}
            </span>
          ) : (
            <span aria-hidden className={`h-5 w-1.5 shrink-0 rounded-full ${a.bar}`} />
          )}
          <h2 id={headingId} className="text-base font-semibold sm:text-lg">
            {title}
          </h2>
        </div>
        {action ? <div className="shrink-0 pt-0.5">{action}</div> : null}
      </div>
      {description ? <p className="mt-1.5 text-sm text-muted">{description}</p> : null}
      <div className="mt-3 flex flex-col gap-3">{children}</div>
    </section>
  );
}

/** "View all →" style link for a Section header. */
export const sectionLinkClass =
  'inline-flex min-h-9 items-center gap-1 rounded-lg px-2 text-sm font-medium text-brand hover:bg-brand-soft';
